import { PROFILE_LABELS, PROFILE_ORDER, type UserProfile } from '@/shared/session/profiles';

import type { AdminUser, AdminUserUpdate } from './types';

/**
 * What the card of a person in Usuarios edits before «Guardar cambios» (contract v10.58,
 * ADR 0055 of the back). The card keeps these and nothing is sent until the button: one
 * `PATCH` with what changed, one password of the elevated mode, one line per change in the
 * access log, and a summary before that says what the person stops seeing.
 */
export interface PersonDraft {
  full_name: string;
  mailboxes: string[];
  profile: UserProfile;
  is_admin: boolean;
  can_send_payroll: boolean;
  is_superadmin: boolean;
}

export function draftOf(user: AdminUser): PersonDraft {
  return {
    full_name: user.full_name ?? '',
    mailboxes: [...(user.mailboxes ?? [])].sort(),
    profile: user.profile,
    is_admin: user.is_admin,
    can_send_payroll: user.can_send_payroll === true,
    is_superadmin: user.is_superadmin,
  };
}

export interface ChangeLine {
  text: string;
  /** Takes something away: painted in the warning tone so it is read before saving. */
  narrows: boolean;
}

/** The sentence of each change and the payload that makes it. Empty: nothing to save. */
export function changesOf(
  user: AdminUser,
  draft: PersonDraft,
): { payload: AdminUserUpdate; lines: ChangeLine[] } {
  const before = draftOf(user);
  const payload: AdminUserUpdate = {};
  const lines: ChangeLine[] = [];

  const name = draft.full_name.trim();
  if (name !== before.full_name.trim() && name !== '') {
    payload.full_name = name;
    lines.push({ text: `Se llamará «${name}»`, narrows: false });
  }

  const added = draft.mailboxes.filter((each) => !before.mailboxes.includes(each));
  const removed = before.mailboxes.filter((each) => !draft.mailboxes.includes(each));
  if (added.length || removed.length) {
    payload.mailboxes = [...draft.mailboxes].sort();
    for (const each of added) lines.push({ text: `Pasa a ver ${each}`, narrows: false });
    for (const each of removed) lines.push({ text: `Deja de ver ${each}`, narrows: true });
    if (draft.mailboxes.length === 0 && !draft.is_superadmin) {
      lines.push({ text: 'Se queda sin buzones: su bandeja saldrá vacía', narrows: true });
    }
  }

  if (draft.profile !== before.profile) {
    payload.profile = draft.profile;
    // PROFILE_ORDER goes from the one that sees most to the one that sees least.
    const narrows = PROFILE_ORDER.indexOf(draft.profile) > PROFILE_ORDER.indexOf(before.profile);
    lines.push({
      text: `Perfil: de ${PROFILE_LABELS[before.profile]} a ${PROFILE_LABELS[draft.profile]}`,
      narrows,
    });
  }

  if (draft.is_admin !== before.is_admin) {
    payload.is_admin = draft.is_admin;
    lines.push(
      draft.is_admin
        ? { text: 'Pasa a administrar: Usuarios, Buzones, Destinos y Activación', narrows: false }
        : { text: 'Deja de administrar', narrows: true },
    );
  }

  if (draft.can_send_payroll !== before.can_send_payroll) {
    payload.can_send_payroll = draft.can_send_payroll;
    lines.push(
      draft.can_send_payroll
        ? { text: 'Pasa a ver y enviar las nóminas', narrows: false }
        : { text: 'Deja de ver y enviar las nóminas', narrows: true },
    );
  }

  if (draft.is_superadmin !== before.is_superadmin) {
    payload.is_superadmin = draft.is_superadmin;
    lines.push(
      draft.is_superadmin
        ? { text: 'Pasa a superadministrador: todos los buzones y la plataforma', narrows: false }
        : { text: 'Deja de ser superadministrador', narrows: true },
    );
  }

  return { payload, lines };
}

/** «compras» for `compras@ferreteriacubas.com`: what a chip of the list says. The whole
 * address goes in its title. Two mailboxes with the same name in different domains keep
 * the domain, or the chips would read the same. */
export function mailboxChips(mailboxes: readonly string[]): { label: string; title: string }[] {
  const local = (address: string) => address.split('@')[0] ?? address;
  return mailboxes.map((address) => {
    const name = local(address);
    const clash = mailboxes.some((other) => other !== address && local(other) === name);
    return { label: clash ? address : name, title: address };
  });
}

/** Two letters for the avatar of a person: «VA» for Víctor Álvarez, «PA» for paulo@. */
export function initialsOf(user: Pick<AdminUser, 'full_name' | 'email'>): string {
  const words = (user.full_name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
  const base = words[0] ?? user.email.split('@')[0] ?? '?';
  return base.slice(0, 2).toUpperCase();
}
